import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Paper,
  TextField,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { alpha, darken, lighten } from '@mui/material/styles';
import { Plus } from 'lucide-react';

import { getCategories } from '../utils/api';
import CategoryEditorDialog from './CategoryEditorDialog';

const filter = createFilterOptions();
const UNCATEGORIZED = { id: null, name: 'no-category', label: 'No category', color: null };

const normalizeName = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

const CategoryPickerPaper = React.forwardRef(function CategoryPickerPaper(
  { children, onAddCategory, ...props },
  ref,
) {
  return (
    <Paper {...props} ref={ref}>
      <Box
        sx={(theme) => ({
          position: 'relative',
          zIndex: 1,
          p: 1,
          pb: 1.25,
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <Button
          type="button"
          fullWidth
          className="category-picker-add-button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onAddCategory}
          startIcon={<Plus size={18} strokeWidth={3} />}
          sx={(theme) => ({
            m: 0,
            minHeight: 48,
            justifyContent: 'flex-start',
            px: 1.5,
            color: theme.palette.background.default,
            border: `2px solid ${theme.palette.secondary.main}`,
            borderRadius: 2,
            background:
              `linear-gradient(135deg, ${darken(theme.palette.secondary.main, 0.52)}, ` +
              `${darken(theme.palette.secondary.main, 0.72)})`,
            boxShadow:
              `0 4px 0 ${darken(theme.palette.secondary.main, 0.5)}, ` +
              `0 0 14px ${alpha(theme.palette.secondary.main, 0.3)}`,
            transition: 'transform 130ms ease, box-shadow 130ms ease',
            '&:hover, &:focus-visible': {
              transform: 'translate(-2px, -2px)',
              borderColor: theme.palette.secondary.main,
              background:
                `linear-gradient(135deg, ${darken(theme.palette.secondary.main, 0.42)}, ` +
                `${darken(theme.palette.secondary.main, 0.62)})`,
              boxShadow:
                `2px 6px 0 ${darken(theme.palette.secondary.main, 0.5)}, ` +
                `0 0 20px ${alpha(theme.palette.secondary.main, 0.48)}`,
            },
            '&:active': {
              transform: 'translateY(2px)',
              boxShadow:
                `0 1px 0 ${darken(theme.palette.secondary.main, 0.5)}, ` +
                `0 0 10px ${alpha(theme.palette.secondary.main, 0.32)}`,
            },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          })}
        >
          Add new category
        </Button>
      </Box>
      {children}
    </Paper>
  );
});

export default function CategoryPicker({
  idPrefix = 'category-picker',
  label = 'Category',
  value = null,
  onChange,
  categories: controlledCategories,
  onCategoriesChange,
  showManageLink = true,
}) {
  const [loadedCategories, setLoadedCategories] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [createName, setCreateName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const categories = controlledCategories || loadedCategories;

  useEffect(() => {
    if (controlledCategories) return;
    getCategories()
      .then((response) => setLoadedCategories(response.categories || []))
      .catch(() => setLoadedCategories([]));
  }, [controlledCategories]);

  const options = useMemo(() => [UNCATEGORIZED, ...categories], [categories]);
  const selected = options.find((option) => option.id === value) || UNCATEGORIZED;

  const publishCategories = (next) => {
    setLoadedCategories(next);
    onCategoriesChange?.(next);
  };

  const openCreateDialog = () => {
    setCreateName(normalizeName(searchText));
    setDialogOpen(true);
  };

  return (
    <Box>
      <Autocomplete
        id={idPrefix}
        value={selected}
        options={options}
        getOptionLabel={(option) => option.inputValue || option.label || option.name || ''}
        isOptionEqualToValue={(option, candidate) => option.id === candidate.id}
        filterOptions={(available, params) => filter(available, params)}
        noOptionsText="No categories match"
        onInputChange={(_event, inputValue, reason) => {
          if (reason === 'input') setSearchText(inputValue);
          if (reason === 'clear') setSearchText('');
        }}
        onChange={(_event, option) => {
          onChange(option?.id ?? null, option || UNCATEGORIZED);
        }}
        renderOption={(props, option) => (
          <li
            {...props}
            key={String(option.id)}
          >
            <Box
              className="category-picker-option-icon"
              sx={(theme) => ({
                width: 24,
                height: 24,
                flex: '0 0 24px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: theme.palette.primary.main,
                bgcolor: option.color || alpha(theme.palette.primary.main, 0.12),
                border: `2px solid ${option.color || alpha(theme.palette.primary.main, 0.65)}`,
                boxShadow: option.color ? `0 0 8px ${alpha(option.color, 0.5)}` : 'none',
              })}
            />
            <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {option.label || option.name}
            </Box>
          </li>
        )}
        renderInput={(params) => <TextField {...params} label={label} />}
        slots={{ paper: CategoryPickerPaper }}
        slotProps={{
          paper: {
            elevation: 0,
            onAddCategory: openCreateDialog,
            sx: (theme) => ({
              mt: 1,
              p: 0,
              overflow: 'visible',
              border: `2px solid ${alpha(theme.palette.primary.main, 0.8)}`,
              borderRadius: 3,
              background:
                `linear-gradient(145deg, ${lighten(theme.palette.background.paper, 0.1)} 0%, ` +
                `${theme.palette.background.paper} 55%, ` +
                `${darken(theme.palette.secondary.main, 0.82)} 100%)`,
              boxShadow:
                `0 7px 0 ${darken(theme.palette.primary.main, 0.55)}, ` +
                `0 0 22px ${alpha(theme.palette.primary.main, 0.28)}`,
            }),
          },
          listbox: {
            sx: (theme) => ({
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              maxHeight: 300,
              p: 1,
              scrollbarWidth: 'thin',
              scrollbarColor:
                `${alpha(theme.palette.secondary.main, 0.75)} ${alpha(theme.palette.background.default, 0.75)}`,
              '& .MuiAutocomplete-option': {
                minHeight: 44,
                gap: 1.25,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                borderRadius: 2,
                color: theme.palette.primary.main,
                backgroundColor: lighten(theme.palette.background.paper, 0.04),
                transition:
                  'transform 130ms ease, border-color 130ms ease, background-color 130ms ease, box-shadow 130ms ease',
                '&:hover, &[data-focus="true"], &.Mui-focused': {
                  transform: 'translateX(3px)',
                  borderColor: alpha(theme.palette.primary.main, 0.75),
                  backgroundColor: darken(theme.palette.primary.main, 0.76),
                  boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                },
                '&[aria-selected="true"]': {
                  color: theme.palette.primary.main,
                  borderColor: alpha(theme.palette.primary.main, 0.9),
                  backgroundColor: `${darken(theme.palette.primary.main, 0.7)} !important`,
                },
              },
              '@media (prefers-reduced-motion: reduce)': {
                '& .MuiAutocomplete-option': { transition: 'none' },
              },
            }),
          },
        }}
        sx={(theme) => ({
          '& .MuiOutlinedInput-root': {
            minHeight: 54,
            borderRadius: 3,
            background:
              `linear-gradient(100deg, ${darken(theme.palette.primary.main, 0.79)}, ` +
              `${theme.palette.background.default} 34%, ` +
              `${darken(theme.palette.secondary.main, 0.84)})`,
            boxShadow: `inset 0 0 18px rgba(0, 0, 0, 0.35)`,
            transition: 'transform 150ms ease, box-shadow 150ms ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: alpha(theme.palette.primary.main, 0.78),
            },
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow:
                `inset 0 0 18px rgba(0, 0, 0, 0.3), ` +
                `0 0 16px ${alpha(theme.palette.primary.main, 0.22)}`,
            },
            '&.Mui-focused': {
              transform: 'translateY(-1px)',
              boxShadow:
                `0 4px 0 ${darken(theme.palette.primary.main, 0.55)}, ` +
                `0 0 20px ${alpha(theme.palette.primary.main, 0.38)}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          },
          '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
            color: theme.palette.secondary.main,
            transition: 'transform 150ms ease, filter 150ms ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.secondary.main, 0.12),
              filter: `drop-shadow(0 0 5px ${alpha(theme.palette.secondary.main, 0.7)})`,
            },
          },
          '& .MuiAutocomplete-popupIndicatorOpen': {
            transform: 'rotate(180deg)',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.primary.main,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& .MuiOutlinedInput-root, & .MuiAutocomplete-popupIndicator': {
              transition: 'none',
            },
          },
        })}
      />
      {showManageLink && (
        <Button
          size="small"
          component="a"
          href="/categories"
          sx={(theme) => ({
            mt: 0.75,
            ml: 0.5,
            p: 0.5,
            minWidth: 0,
            border: 0,
            borderRadius: 1,
            color: theme.palette.secondary.main,
            fontSize: '0.7rem',
            background: 'transparent',
            '&:hover': {
              color: theme.palette.primary.main,
              background: alpha(theme.palette.primary.main, 0.08),
              boxShadow: 'none',
            },
          })}
        >
          Manage categories
        </Button>
      )}
      <CategoryEditorDialog
        open={dialogOpen}
        initialName={createName}
        onClose={() => setDialogOpen(false)}
        onSaved={(category) => {
          const next = [...categories.filter((item) => item.id !== category.id), category]
            .sort((a, b) => a.name.localeCompare(b.name));
          publishCategories(next);
          onChange(category.id, category);
          setDialogOpen(false);
        }}
      />
    </Box>
  );
}
